




















































import { Component, Prop, Vue } from "vue-property-decorator";
import { MiraDotRow, niceAxisMax } from "@/utils/MiraDryingView";

/**
 * Mean and median per HyDry category on one shared axis: a dot plot (market research 2026-09-30:
 * a few categories compare cleanly as dots, and a duration wants its median beside the mean, since a
 * few long jobs drag the mean). ● is the mean, ◆ the median, each in the category's colour with a 2px
 * surface ring so they stay apart where they overlap; both values are also printed, so the shapes
 * never carry a number alone.
 */
@Component
export default class MiraDotPlotComponent extends Vue {
  @Prop({ required: true })
  rows!: MiraDotRow[];

  @Prop({ required: true })
  trackColor!: string;

  /** The card's surface, for the ring that keeps overlapping marks apart. */
  @Prop({ required: true })
  surfaceColor!: string;

  /** The unit label for the axis end, e.g. "30 Tage". */
  @Prop({ required: true })
  formatAxis!: (value: number) => string;

  /** The count column's heading. Trocknung counts dryings, the default; Prozess counts projects. */
  @Prop({ default: null })
  countLabel!: string | null;

  /** The label column's width: 72px fits a device type, a phase name needs more. */
  @Prop({ default: "72px" })
  labelWidth!: string;

  /** Whether to draw the faint P15 to P85 range behind the marks (`VIEW_MIRA_PERCENTILES`). */
  @Prop({ default: false })
  showPercentiles!: boolean;

  get gridStyle(): Record<string, string> {
    return { gridTemplateColumns: `${this.labelWidth} 1fr 80px 80px 88px` };
  }

  /** The second column's heading: always the median (● mean, ◆ median, no third statistic). */
  get secondColumnLabel(): string {
    return this.$t("mira.drying.dotPlot.median").toString();
  }

  /** The value the second mark sits at: the row's median. */
  secondValue(row: MiraDotRow): number {
    return row.median;
  }

  /** The second value column's text: the row's median text. */
  secondText(row: MiraDotRow): string {
    return row.medianText;
  }

  /**
   * Scaled to the rows that draw marks: a row with a note has none to fit. The P85 is included when
   * the range is shown, so the band always fits on the axis.
   */
  get axisMax(): number {
    const drawn = this.rows.filter((row) => !row.note);
    const values = drawn.flatMap((row) => {
      const points = [row.mean, this.secondValue(row)];
      if (this.showPercentiles && this.hasRange(row)) {
        points.push(row.p85 as number);
      }
      return points;
    });
    return niceAxisMax(Math.max(1, ...values));
  }

  get axisMaxText(): string {
    return this.formatAxis(this.axisMax);
  }

  hasRange(row: MiraDotRow): boolean {
    return row.p15 !== null && row.p15 !== undefined && row.p85 !== null && row.p85 !== undefined;
  }

  fraction(value: number): number {
    return Math.max(0, Math.min(1, value / this.axisMax));
  }

  position(value: number): string {
    return `${this.fraction(value) * 100}%`;
  }

  rangeWidth(row: MiraDotRow): string {
    return `${Math.max(0, this.fraction(row.p85 as number) - this.fraction(row.p15 as number)) * 100}%`;
  }
}
